// hooks/useSharedPLCalculations.ts
import { useMemo } from 'react';
import { useStateSelector } from '../data/global/dataSelectors';
import { getQuotesMetadata } from '../data/global/selectors/quoteSelector';
import { PLResult, usePLWorker } from './usePLWorker';
import { TodaysFilledOrders, GetSymbolsFromOrders } from '@tradingblock/api';

export function useSharedPLCalculations() {
  // Get shared data at the hook level
  const positions = useStateSelector(state => state.positions.positions);
  const orders = useStateSelector(state => state.account.orders || []);
  const subAccountFilterId = useStateSelector(s => s.ui.dashboard.subAccountFilterId);

  // Get today's filled orders and their symbols
  const todaysOrders = useMemo(() => {
    const filtered = TodaysFilledOrders(orders);
    if (subAccountFilterId && subAccountFilterId !== -1) {
      return filtered.filter(o => o.SubaccountId === subAccountFilterId);
    }
    return filtered;
  }, [orders, subAccountFilterId]);

  const orderSymbols = useMemo(() => GetSymbolsFromOrders(todaysOrders), [todaysOrders]);

  // Memoize traded symbols set for efficient lookups
  const tradedSymbolsSet = useMemo(() => new Set(orderSymbols), [orderSymbols]);

  // Include symbols from both positions and today's orders
  const positionSymbols = useMemo(
    () => Array.from(new Set((positions || []).map(p => p.Symbol))).filter(Boolean) as string[],
    [positions]
  );

  // Combine position symbols with order symbols to ensure we fetch quotes for traded symbols
  const allSymbols = useMemo(
    () => Array.from(new Set([...positionSymbols, ...orderSymbols])).filter(Boolean) as string[],
    [positionSymbols, orderSymbols]
  );

  const quotes = useStateSelector(state => getQuotesMetadata(state, allSymbols)) || {};
  const accountId = useStateSelector(state => state.account.accountId);
  const marketSchedule = useStateSelector(state => state.ui.marketSchedule);

  // Single worker instance for all components - now handles both positions and orders
  const { plMap, isLoading } = usePLWorker(
    positions || [],
    Object.values(quotes || {}),
    accountId ? String(accountId) : undefined,
    subAccountFilterId,
    marketSchedule,
    todaysOrders // Pass today's orders for trading P/L calculation
  );

  // Memoize symbol-to-underlying maps to avoid rebuilding on every render
  const symbolMaps = useMemo(() => {
    const symbolToUnderlying: Record<string, string> = {};
    const underlyingToSymbols: Record<string, string[]> = {};

    // Process positions and orders in a single combined pass
    for (const p of positions) {
      if (p.Symbol && p.UnderlyingSymbol) {
        symbolToUnderlying[p.Symbol] = p.UnderlyingSymbol;
        if (!underlyingToSymbols[p.UnderlyingSymbol]) {
          underlyingToSymbols[p.UnderlyingSymbol] = [];
        }
        if (!underlyingToSymbols[p.UnderlyingSymbol].includes(p.Symbol)) {
          underlyingToSymbols[p.UnderlyingSymbol].push(p.Symbol);
        }
      }
    }

    for (const o of todaysOrders) {
      if (o.Legs && o.UnderlyingSymbol) {
        for (const leg of o.Legs) {
          if (leg.Symbol) {
            symbolToUnderlying[leg.Symbol] = o.UnderlyingSymbol;
            if (!underlyingToSymbols[o.UnderlyingSymbol]) {
              underlyingToSymbols[o.UnderlyingSymbol] = [];
            }
            if (!underlyingToSymbols[o.UnderlyingSymbol].includes(leg.Symbol)) {
              underlyingToSymbols[o.UnderlyingSymbol].push(leg.Symbol);
            }
          }
        }
      }
    }

    return { symbolToUnderlying, underlyingToSymbols };
  }, [positions, todaysOrders]);

  // Memoize the computed values
  // plWorker now handles both position P/L and trading P/L (for closed positions)
  const computedValues = useMemo(() => {
    if (isLoading) {
      return {
        dailyPL: 0,
        totalPL: 0,
        positionValue: 0,
        totals: {
          all: { trading: 0, position: 0, total: 0 },
          tradedToday: { trading: 0, position: 0, total: 0 },
        },
        todayTradingPnl: 0,
        bySymbol: {} as Record<string, { trading: number; position: number; total: number; symbol: string }>,
        aggregatePositionPerformances: (positionIds: string[]) => ({ pnl: 0 }),
      };
    }

    const values = Object.values(plMap);
    // plWorker calculates dayPL and totalPL for both positions and closed positions (trading P/L)
    const dailyPL = values.reduce((sum, data) => sum + (data.dayPL || 0), 0);
    const totalPL = values.reduce((sum, data) => sum + (data.totalPL || 0), 0);
    const positionValue = values.reduce((sum, data) => sum + (data.currentValue || 0), 0);

    // Calculate trading P/L and position P/L separately for totals
    // We need to reconstruct this from plWorker data to match performanceBySymbolReducer structure
    let totalTradingPL = 0;
    let totalPositionPL = 0;
    let tradedTodayTradingPL = 0;
    let tradedTodayPositionPL = 0;
    const bySymbolMap: Record<
      string,
      { trading: number; position: number; total: number; symbol: string; underlyingSymbol: string }
    > = {};

    // Use memoized symbol maps
    const { underlyingToSymbols } = symbolMaps;

    // Process each underlyingSymbol's data
    for (const [underlyingSymbol, data] of Object.entries(plMap)) {
      if (!data || typeof data !== 'object') continue;

      const hasPositions = data.positions && Object.keys(data.positions).length > 0;
      const tradingPL = data.tradingPL || 0; // Only set for closed positions
      const dayPL = data.dayPL || 0;

      if (hasPositions) {
        // Has positions: trading P/L = 0, position P/L = dayPL (from positions)
        totalPositionPL += dayPL;

        // Build bySymbol map for each position symbol
        for (const [symbol, posData] of Object.entries(data.positions)) {
          if (!bySymbolMap[symbol]) {
            bySymbolMap[symbol] = { trading: 0, position: 0, total: 0, symbol, underlyingSymbol };
          }
          const posDayPL = (posData && posData.dayPL) || 0;
          bySymbolMap[symbol].position += posDayPL;
          bySymbolMap[symbol].total += posDayPL;

          // Check if this symbol was traded today
          if (tradedSymbolsSet.has(symbol)) {
            tradedTodayPositionPL += posDayPL;
          }
        }
      } else if (tradingPL !== 0 || dayPL !== 0) {
        // No positions but has orders (closed positions): trading P/L = tradingPL, position P/L = dayPL - tradingPL
        const positionPL = dayPL - tradingPL;
        totalTradingPL += tradingPL;
        totalPositionPL += positionPL;

        // Use per-symbol P/L data from plWorker (more accurate than dividing equally)
        const symbolPL = (data as any).symbolPL || {};
        const tradedSymbols = (data as any).tradedSymbols || [];

        // If we have per-symbol P/L data, use it directly
        if (Object.keys(symbolPL).length > 0) {
          for (const [symbol, plData] of Object.entries(symbolPL)) {
            const symbolPLData = plData as { tradingPL: number; positionPL: number; totalPL: number };
            if (!bySymbolMap[symbol]) {
              bySymbolMap[symbol] = { trading: 0, position: 0, total: 0, symbol, underlyingSymbol };
            }
            // Use actual per-symbol P/L values (already converted to dollars by plWorker)
            bySymbolMap[symbol].trading += symbolPLData.tradingPL;
            bySymbolMap[symbol].position += symbolPLData.positionPL;
            bySymbolMap[symbol].total += symbolPLData.totalPL;

            // Check if this symbol was traded today
            if (tradedSymbolsSet.has(symbol)) {
              tradedTodayTradingPL += symbolPLData.tradingPL;
              tradedTodayPositionPL += symbolPLData.positionPL;
            }
          }
        } else {
          // Fallback: Get symbols that were traded for this underlying (stored by plWorker)
          const symbolsForUnderlying =
            tradedSymbols.length > 0 ? tradedSymbols : underlyingToSymbols[underlyingSymbol] || [];

          // For closed positions, distribute P/L across the symbols that were traded
          // This is a fallback if per-symbol data isn't available
          if (symbolsForUnderlying.length > 0) {
            const symbolCount = symbolsForUnderlying.length;
            for (const symbol of symbolsForUnderlying) {
              if (!bySymbolMap[symbol]) {
                bySymbolMap[symbol] = { trading: 0, position: 0, total: 0, symbol, underlyingSymbol };
              }
              // Distribute P/L across symbols
              bySymbolMap[symbol].trading += tradingPL / symbolCount;
              bySymbolMap[symbol].position += positionPL / symbolCount;
              bySymbolMap[symbol].total += dayPL / symbolCount;
            }
          }

          // Check if any symbol for this underlying was traded today
          if (symbolsForUnderlying.some((s: string) => tradedSymbolsSet.has(s))) {
            tradedTodayTradingPL += tradingPL;
            tradedTodayPositionPL += positionPL;
          }
        }
      }
    }

    // Aggregate position performances by positionId
    // Note: plWorker doesn't track positionId directly, so we need to match positions by their data
    // This is a limitation - for now we'll aggregate all positions' P/L
    // To properly implement this, we'd need to enhance plWorker to include positionId
    const aggregatePositionPerformances = (positionIds: string[]) => {
      // Match positions by finding them in the positions array
      const matchingPositions = positions.filter(p => p.PositionId && positionIds.includes(p.PositionId));
      let totalPnl = 0;

      for (const pos of matchingPositions) {
        if (!pos.Symbol || !pos.UnderlyingSymbol) continue;
        const underlyingData = plMap[pos.UnderlyingSymbol];
        if (underlyingData && underlyingData.positions && underlyingData.positions[pos.Symbol]) {
          const posData = underlyingData.positions[pos.Symbol];
          totalPnl += (posData && posData.totalPL) || 0;
        }
      }

      return { pnl: totalPnl };
    };

    return {
      dailyPL,
      totalPL,
      positionValue,
      totals: {
        all: {
          trading: totalTradingPL,
          position: totalPositionPL,
          total: totalTradingPL + totalPositionPL,
        },
        tradedToday: {
          trading: tradedTodayTradingPL,
          position: tradedTodayPositionPL,
          total: tradedTodayTradingPL + tradedTodayPositionPL,
        },
      },
      todayTradingPnl: totalTradingPL,
      bySymbol: bySymbolMap,
      aggregatePositionPerformances,
    };
  }, [plMap, isLoading, positions, symbolMaps, tradedSymbolsSet]);

  return {
    plMap,
    isLoading,
    ...computedValues,
  };
}
